// Where the Insights tab gets its numbers (demo build). ONE request for the range being viewed and ONE for the interval before it: `getInsightsReport`, the aggregate the real server would answer with
// (the demo's own is demoBackend.ts). Kept in its own module so the rules (insightsRules.ts) never know where counts come from.
import { INITIAL_FILTERS } from "../constants/commonConstants";
import { previousInterval, previousLabel } from "../footerTrends/previousInterval";
import { session } from "../services/session";
import { readableLabel } from "../util/helper";
import storage from "../util/storage";
import { isUnknownBucketName } from "../util/unknownBucket";
import { Dimension, Group, ProfileSide, SiteTotals } from "./insightsRules";
import type { PathAnswer } from "./pathCore";

export interface InsightsRange {
  name: string;
  startMs: number;
  endMs: number;
  /** the range runs up to now */
  ongoing: boolean;
}

/** The characteristics the dashboard can already group by (its "By ..." filters), in the order the filter menu has them. */
export const INSIGHT_DIMENSIONS: { menuType: string; title: string }[] = INITIAL_FILTERS.filter((f: any) => f.menuType && f.menuType !== "none").map((f: any) => ({ menuType: f.menuType, title: f.name }));

const SEG_SEPARATOR = "\u0001";

/** the viewer's time zone (e.g. "America/New_York"): the server counts hours, weekdays and days in it, as the browser's own clock does for the demo */
export const viewerTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch (error) {
    return undefined;
  }
};

/** what the server answers for one range */
export interface RawReport {
  /** sessions by PEOPLE (the report leaves automated visits out) */
  sessions: number;
  /** every session, people and automated */
  all?: number;
  /** automated sessions by kind (search_engine_bot, ai_crawler, other_bot) */
  automated?: Record<string, number>;
  segs: Record<string, { n: number; newN: number; conv: number; bounced: number; durMs: number }>;
  hours: number[];
  hourConv: number[];
  /** people's bounces and total time on site (ms) by arrival hour: the hourly trend of a one-day range */
  hourBounced?: number[];
  hourDurMs?: number[];
  dows: number[];
  dowConv: number[];
  /** the hour-of-week grid the heatmap draws: index = weekday * 24 + local hour, Sunday first (168 numbers) */
  dowHours?: number[];
  /** people's sessions, conversions, bounces and time on site for each local day of the range: the trend of a range of two days or more */
  daily?: { day: number; n: number; conv: number; bounced: number; durMs: number }[];
  landings: Record<string, { n: number; bounced: number; conv: number }>;
  exits: Record<string, number>;
  views: Record<string, number>;
  profile: { conv: ProfileSide; other: ProfileSide };
  /** the most common route through the site (an older server does not send it) */
  path?: PathAnswer;
}

/** one range's report as the tab uses it */
export interface Report {
  site: SiteTotals;
  /** every session in the range and the automated ones among them (by kind): what the report left out */
  all: number;
  automated: Record<string, number>;
  /** each characteristic's groups (no previous interval attached here) */
  groups: { menuType: string; title: string; groups: Group[] }[];
  hours: number[];
  hourConv: number[];
  hourBounced: number[];
  hourDurMs: number[];
  dows: number[];
  dowConv: number[];
  dowHours: number[];
  daily: NonNullable<RawReport["daily"]>;
  landings: RawReport["landings"];
  exits: RawReport["exits"];
  views: RawReport["views"];
  profile: RawReport["profile"];
  /** the most common route, or null when the server did not send one */
  path: PathAnswer | null;
}

const displayOf = (menuType: string, name: string): string => (menuType === "referrer" && name === "/" ? "Direct" : readableLabel(menuType, name));

/** the route as answered, or null for anything that is not one (an older server: the tab simply has no route card) */
export const pathFrom = (raw: unknown): PathAnswer | null => {
  const p = raw as PathAnswer | null | undefined;
  if (!p || typeof p !== "object" || !Array.isArray(p.steps) || typeof p.visits !== "number") return null;
  const steps = p.steps.filter((s) => s && typeof s.page === "string" && Number.isFinite(s.reached)).map((s) => ({ page: s.page, reached: Number(s.reached), ended: Number(s.ended) || 0, run: Number(s.run) || 1, lead: Number(s.lead) || 0, others: Array.isArray(s.others) ? s.others.filter((o) => o && typeof o.page === "string" && Number.isFinite(o.reached)).map((o) => ({ page: o.page, reached: Number(o.reached) })) : [] }));

  return { visits: p.visits, steps, stop: p.stop };
};

/** A raw answer as the tab's report: the site's totals, and every characteristic's groups (groups with no name are dropped). Pure, so it can be tested. */
export const reportFromRaw = (raw: Partial<RawReport> | null | undefined): Report => {
  const segs = raw?.segs && typeof raw.segs === "object" ? raw.segs : {};
  const all = segs[`none${SEG_SEPARATOR}All visitors`];
  const site: SiteTotals = { sessions: all?.n || 0, converted: all?.conv || 0, bounced: all?.bounced || 0, durMs: all?.durMs || 0, newN: all?.newN || 0 };
  const groups = INSIGHT_DIMENSIONS.map(({ menuType, title }) => ({
    menuType,
    title,
    groups: Object.entries(segs)
      .filter(([key]) => key.startsWith(`${menuType}${SEG_SEPARATOR}`))
      .map(([key, s]) => ({ name: key.slice(menuType.length + 1), s }))
      .filter(({ name }) => !!name && name !== "null" && name !== "undefined")
      .map(({ name, s }): Group => ({ name, display: displayOf(menuType, name), unknown: isUnknownBucketName(name, menuType), sessions: s.n || 0, converted: s.conv || 0, bounced: s.bounced || 0, durMs: s.durMs || 0, newN: s.newN || 0 })),
  }));
  const zero = (): ProfileSide => ({ n: 0, pages: 0, durMs: 0, newN: 0, mobile: 0 });

  return {
    site,
    all: Number(raw?.all) || site.sessions,
    automated: raw?.automated && typeof raw.automated === "object" ? raw.automated : {},
    groups,
    hours: raw?.hours || [],
    hourConv: raw?.hourConv || [],
    hourBounced: raw?.hourBounced || [],
    hourDurMs: raw?.hourDurMs || [],
    dows: raw?.dows || [],
    dowConv: raw?.dowConv || [],
    dowHours: raw?.dowHours || [],
    daily: Array.isArray(raw?.daily) ? (raw?.daily as NonNullable<RawReport["daily"]>) : [],
    landings: raw?.landings || {},
    exits: raw?.exits || {},
    views: raw?.views || {},
    profile: { conv: raw?.profile?.conv || zero(), other: raw?.profile?.other || zero() },
    path: pathFrom(raw?.path),
  };
};

/** The characteristics with the interval before attached, ready for the rules. */
export const dimensionsOf = (current: Report, previous: Report | null): Dimension[] =>
  current.groups.map((d) => ({ menuType: d.menuType, title: d.title, groups: d.groups, previous: previous ? previous.groups.find((p) => p.menuType === d.menuType)?.groups ?? null : null }));

const fetchReport = async (startMs: number, endMs: number): Promise<Report> => {
  const reply = await session({
    id: 0,
    jsonprc: "2.0",
    method: "getInsightsReport",
    params: { endDate: new Date(endMs).toISOString(), startDate: new Date(startMs).toISOString(), siteId: storage.get("siteId"), timezone: viewerTimeZone() },
  });

  return reportFromRaw(reply?.data?.result);
};

/** what the stories need about one group: its totals, its days, its hours, and what its visits have in common with each other characteristic */
export interface GroupProfile {
  menuType: string;
  value: string;
  n: number;
  conv: number;
  bounced: number;
  durMs: number;
  hours: number[];
  cross: Record<string, Record<string, { n: number; conv: number }>>;
  daily: { day: number; n: number; conv: number; bounced: number }[];
}

/** An answer as profiles: an older or odd answer gives empty ones, never undefined (a story just has less to say). */
export const profilesFromRaw = (raw: unknown): GroupProfile[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .filter((p) => p && typeof p.menuType === "string" && typeof p.value === "string")
    .map((p) => ({
      menuType: p.menuType,
      value: p.value,
      n: Number(p.n) || 0,
      conv: Number(p.conv) || 0,
      bounced: Number(p.bounced) || 0,
      durMs: Number(p.durMs) || 0,
      hours: Array.isArray(p.hours) && p.hours.length === 24 ? p.hours.map((x: unknown) => Number(x) || 0) : new Array(24).fill(0),
      cross: p.cross && typeof p.cross === "object" ? p.cross : {},
      daily: Array.isArray(p.daily) ? p.daily : [],
    }));
};

/** The profiles of the groups the stories are about, for the range being looked at (one request for all of them). */
export const loadGroupProfiles = async (range: InsightsRange, groups: { menuType: string; value: string }[]): Promise<GroupProfile[]> => {
  if (groups.length === 0) return [];
  const end = range.ongoing ? Date.now() : range.endMs;
  const reply = await session({
    id: 0,
    jsonprc: "2.0",
    method: "getInsightsGroupProfiles",
    params: { endDate: new Date(end).toISOString(), startDate: new Date(range.startMs).toISOString(), siteId: storage.get("siteId"), groups, timezone: viewerTimeZone() },
  });

  return profilesFromRaw(reply?.data?.result);
};

export interface LoadedInsights {
  current: Report;
  previous: Report | null;
  previousLabel: string | null;
  /** why there is nothing to compare with, when previousLabel is null */
  noPrevious: "all" | "history" | "invalid" | null;
  /** the range covers at least a week, so a weekday pattern means something */
  spansWeek: boolean;
}

/** Everything the tab needs for `range`: its report and (when there is an interval before it) that one's. */
export const loadInsights = async (range: InsightsRange, earliestMs: number | null): Promise<LoadedInsights> => {
  const end = range.ongoing ? Date.now() : range.endMs;
  const before = previousInterval(range.name, range.startMs, end, earliestMs);
  const [current, previous] = await Promise.all([fetchReport(range.startMs, end), before.ok ? fetchReport(before.interval.startMs, before.interval.endMs) : Promise.resolve(null)]);
  // (the project's compiler settings do not narrow a union on a boolean flag, so the reason is read through its own type)
  const noPrevious = before.ok ? null : (before as { reason: "all" | "history" | "invalid" }).reason;

  return { current, previous, previousLabel: before.ok ? previousLabel(before.spanDays, range.ongoing) : null, noPrevious, spansWeek: end - range.startMs >= 6.5 * 86400000 };
};
